/**
 * Status da plataforma (doc: docs/funcionalidades/banner-de-status.md).
 *
 * A fonte é o JSON PÚBLICO da status page da Better Stack
 * (`https://status.playprosport.com.br/index.json`, CORS `*`). É o mesmo dado que um
 * webhook empurraria, com uma diferença que decide o desenho: a leitura NÃO passa pela
 * nossa API — e o banner importa justamente quando a nossa API está fora. A queda da
 * própria API já tem sinal próprio (faixa "Conexão instável" da GuardaDeVersaoDoEsquema).
 *
 * Tudo aqui é puro: o componente busca o JSON e chama `lerStatusDaPagina`.
 */

export const URL_DA_PAGINA_DE_STATUS = "https://status.playprosport.com.br";

/** Chave de `localStorage` para ligar o banner fora de PROD (teste manual no HML/dev). */
export const CHAVE_OVERRIDE_DA_PAGINA_DE_STATUS = "pps:status-page-url";

/**
 * `informativo` = só o recado fixo da página (`announcement`), com tudo operacional.
 * Ordem de gravidade em GRAVIDADE.
 */
export type EstadoDaPlataforma = "operacional" | "informativo" | "manutencao" | "degradado" | "fora";

export interface AvisoPublicado {
  id: string;
  titulo: string;
}

export interface StatusDaPlataforma {
  estado: EstadoDaPlataforma;
  /** Nomes públicos dos serviços que não estão operacionais, na ordem da página. */
  afetados: string[];
  /** Recado publicado à mão (aviso de incidente/manutenção em vigor, ou o `announcement`). */
  aviso: AvisoPublicado | null;
  /** Muda quando o quadro muda — é o que faz o banner dispensado voltar. */
  assinatura: string;
}

const ESTADO_DA_BETTER_STACK: Record<string, EstadoDaPlataforma> = {
  operational: "operacional",
  degraded: "degradado",
  downtime: "fora",
  maintenance: "manutencao",
};

const GRAVIDADE: Record<EstadoDaPlataforma, number> = {
  operacional: 0,
  informativo: 1,
  manutencao: 2,
  degradado: 3,
  fora: 4,
};

function texto(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

function instante(v: unknown): number | null {
  const s = texto(v);
  if (!s) return null;
  const t = Date.parse(s);
  return Number.isFinite(t) ? t : null;
}

/**
 * Estado que um aviso publicado impõe, ou `null` se ele não deve acender nada.
 * Só acende o que a página declara como problema (degraded/downtime/maintenance) ou
 * manutenção programada SEM estado. Qualquer outro valor (`resolved`, `operational`, algo
 * novo) é ignorado: aviso resolvido sem `ends_at` não pode prender o banner aceso.
 */
function estadoDoAviso(atributos: Record<string, unknown>): EstadoDaPlataforma | null {
  const bruto = atributos.aggregate_state;
  if (bruto == null || bruto === "") {
    return atributos.report_type === "maintenance" ? "manutencao" : null;
  }
  const mapeado = ESTADO_DA_BETTER_STACK[String(bruto)];
  return mapeado && mapeado !== "operacional" ? mapeado : null;
}

/**
 * Lê o `index.json` da status page. `null` = formato que não reconhecemos (não mostra
 * nada: banner falso de incidente é pior que banner nenhum).
 */
export function lerStatusDaPagina(json: unknown, agora: number = Date.now()): StatusDaPlataforma | null {
  const raiz = json as { data?: { attributes?: Record<string, unknown> }; included?: unknown } | null;
  const atributos = raiz?.data?.attributes;
  if (!atributos || typeof atributos !== "object") return null;

  const geral = ESTADO_DA_BETTER_STACK[String(atributos.aggregate_state ?? "")];
  if (!geral) return null;

  const incluidos = Array.isArray(raiz?.included) ? (raiz!.included as Array<Record<string, unknown>>) : [];

  const afetados = incluidos
    .filter((i) => i?.type === "status_page_resource")
    .map((i) => (i.attributes ?? {}) as Record<string, unknown>)
    .filter((a) => {
      const s = String(a.status ?? "");
      return s !== "" && s !== "operational" && s !== "not_monitored";
    })
    .sort((a, b) => Number(a.position ?? 0) - Number(b.position ?? 0))
    .map((a) => texto(a.public_name))
    .filter((n): n is string => n !== null);

  // Aviso publicado à mão: em vigor (começou e não terminou) e declarando problema.
  let aviso: AvisoPublicado | null = null;
  let doAviso: EstadoDaPlataforma = "operacional";
  for (const i of incluidos) {
    if (i?.type !== "status_report") continue;
    const a = (i.attributes ?? {}) as Record<string, unknown>;
    const inicio = instante(a.starts_at);
    const fim = instante(a.ends_at);
    if (inicio !== null && inicio > agora) continue;
    if (fim !== null && fim <= agora) continue;
    const titulo = texto(a.title);
    const estado = estadoDoAviso(a);
    if (!titulo || !estado) continue;
    if (GRAVIDADE[estado] > GRAVIDADE[doAviso]) {
      aviso = { id: String(i.id ?? titulo), titulo };
      doAviso = estado;
    }
  }

  // Recado fixo da página: só vira texto do banner se nenhum aviso de incidente o fizer.
  const recado = texto(atributos.announcement);
  if (!aviso && recado) {
    aviso = { id: `announcement:${recado}`, titulo: recado };
    doAviso = "informativo";
  }

  const estado = GRAVIDADE[doAviso] > GRAVIDADE[geral] ? doAviso : geral;
  return {
    estado,
    afetados,
    aviso,
    assinatura: [estado, afetados.join(","), aviso?.id ?? ""].join("|"),
  };
}

/** Mostra o banner? Só fora do operacional e se esta assinatura não foi dispensada. */
export function deveMostrarStatus(status: StatusDaPlataforma | null, dispensada: string | null): boolean {
  if (!status || status.estado === "operacional") return false;
  return status.assinatura !== dispensada;
}

/**
 * URL da status page para o ambiente: PROD liga; o resto só com override explícito em
 * `localStorage` (o HML não tem status page própria — mostrar a de PROD no HML confundiria
 * quem testa). Override precisa ser https.
 */
export function urlDaPaginaDeStatus(ambiente: string | null | undefined, override: string | null): string | null {
  const env = (ambiente ?? "").toLowerCase();
  if (env === "prod" || env === "production") return URL_DA_PAGINA_DE_STATUS;
  if (override && /^https:\/\/[^\s/]+/.test(override)) return override.replace(/\/+$/, "");
  return null;
}

/** O mesmo, lendo o override do `localStorage` do navegador (sem storage = sem override). */
export function urlDaPaginaDeStatusNoNavegador(ambiente: string | null | undefined): string | null {
  let override: string | null = null;
  try {
    override = localStorage.getItem(CHAVE_OVERRIDE_DA_PAGINA_DE_STATUS);
  } catch {
    override = null;
  }
  return urlDaPaginaDeStatus(ambiente, override);
}

/** Tradutor mínimo (o `t` do react-i18next serve) — o pacote não depende de i18n. */
export type Traduzir = (chave: string, opcoes?: Record<string, unknown>) => string;

/** Textos do banner a partir das chaves `sistema.status.*` — uma fonte só para os 3 apps. */
export function textosDoAvisoDeStatus(t: Traduzir) {
  return {
    fora: t("sistema.status.fora"),
    degradado: t("sistema.status.degradado"),
    manutencao: t("sistema.status.manutencao"),
    informativo: t("sistema.status.informativo"),
    afetados: (lista: string) => t("sistema.status.afetados", { lista }),
    verStatus: t("sistema.status.verStatus"),
    fechar: t("sistema.status.fechar"),
  };
}
